import { imgUrl } from "../lib/api";

/**
 * ProductImage — drop-in replacement for `<img src={imgUrl(...)} />` that
 * serves a WebP sibling first and falls back to the original jpg/png.
 *
 * Why a wrapper rather than a smart `imgUrl`:
 *   - We never overwrite the legacy /api/static/products/<file>.jpg path
 *     stored in the per-product JSON files (CMS-safe — Decap keeps editing
 *     those JSONs untouched).
 *   - `<picture>` lets the browser pick: WebP for the 97%+ of browsers that
 *     support it, original JPG/PNG for the few remaining (very old iOS,
 *     IE-equivalent in-app webviews).
 *   - The original `<img>` keeps all its layout attributes (Tailwind className,
 *     alt, loading, fetchPriority, decoding, data-testid, style, etc.) so
 *     the surrounding layout never moves.
 *
 * The WebP sibling is generated at build/dev time by scripts and lives at
 * the same path as the original with `.webp` extension (see commit
 * containing this file). If a sibling doesn't exist on disk, the
 * `<source>` 404s silently and the browser falls back to the `<img>`.
 *
 * Props mirror `<img>` exactly. The only extra prop is `path` (alias for
 * the value you'd otherwise pass to `imgUrl(...)` then to `src`).
 */
export const ProductImage = ({
  path,
  alt = "",
  className,
  style,
  loading,
  fetchPriority,
  decoding,
  draggable,
  width,
  height,
  onError,
  onLoad,
  ...rest
}) => {
  const src = imgUrl(path);
  if (!src) return null;

  // Only produce a webp sibling when we point at our own /products/ asset.
  // External URLs (http(s)://…) get a plain <img> — we don't host their WebP.
  const isLocalProduct = src.startsWith("/products/");
  const webpUrl = isLocalProduct ? src.replace(/\.(jpe?g|png)$/i, ".webp") : null;

  const imgProps = {
    src,
    alt,
    className,
    style,
    loading,
    // React 19 still uses the lowercase `fetchpriority` HTML attr; the camelCase
    // `fetchPriority` prop maps to it correctly when set on <img>.
    fetchPriority,
    decoding,
    draggable,
    width,
    height,
    onError,
    onLoad,
    ...rest,
  };

  if (!webpUrl) return <img {...imgProps} />;

  return (
    <picture>
      <source srcSet={webpUrl} type="image/webp" />
      <img {...imgProps} />
    </picture>
  );
};
